import React from "react";
import { Link } from "react-router-dom";
import { Star, ChevronRight, Calendar } from "lucide-react";
import { fmtMoney, fmtPct, fmtScore, fmtDate, recommendationColor } from "../lib/format";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";

export default function StockCard({ stock, prominent = false, onOpen, onToggleFav, onOpenScore, onOpenInsight }) {
  const positive = stock.change_pct >= 0;
  const recClass = recommendationColor(stock.recommendation_label);
  const lastDate = stock.quote_date || (stock.captured_at ? String(stock.captured_at).slice(0, 10) : null);

  return (
    <div
      className={`card-elevated relative p-5 sm:p-6 ${prominent ? "col-span-2 row-span-2" : ""}`}
      data-testid={`stock-card-${stock.ticker.toLowerCase()}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Link
              to={`/acao/${stock.id}`}
              className="font-display text-xl sm:text-2xl font-bold tracking-tight hover:underline underline-offset-4"
              data-testid={`stock-ticker-${stock.ticker.toLowerCase()}`}
            >
              {stock.ticker}
            </Link>
            <Badge variant="outline" className="text-[10px] uppercase tracking-[0.18em]">
              {stock.exchange}
            </Badge>
          </div>
          <div className="text-sm text-muted-foreground truncate">{stock.company_name}</div>
          <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground/80 mt-1">
            {stock.sector}
          </div>
        </div>
        <button
          onClick={() => onToggleFav && onToggleFav(stock)}
          className={`p-2 rounded-md hover:bg-accent transition ${stock.is_favorite ? "text-amber-500" : "text-muted-foreground"}`}
          data-testid={`favorite-toggle-${stock.ticker.toLowerCase()}`}
          aria-label="Favoritar"
        >
          <Star className={`w-4 h-4 ${stock.is_favorite ? "fill-current" : ""}`} />
        </button>
      </div>

      <div className="mt-5 grid grid-cols-2 gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Fechamento</div>
          <div className="font-mono-tab text-lg sm:text-xl font-semibold mt-0.5" data-testid={`stock-price-${stock.ticker.toLowerCase()}`}>
            {fmtMoney(stock.close, stock.currency)}
          </div>
          <div className={`text-xs font-mono-tab mt-0.5 ${positive ? "text-buy" : "text-sell"}`}>
            {fmtPct(stock.change_pct)}
          </div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Score</div>
          <button
            onClick={() => onOpenScore && onOpenScore(stock)}
            className="font-mono-tab text-lg sm:text-xl font-semibold mt-0.5 hover:underline underline-offset-4 block ml-auto"
            data-testid={`stock-score-${stock.ticker.toLowerCase()}`}
          >
            {fmtScore(stock.composite_score)}
          </button>
          <div className={`mt-1 inline-block px-2 py-0.5 rounded-md border text-[11px] font-medium ${recClass}`}>
            {stock.recommendation_label}
          </div>
        </div>
      </div>

      <div className="mt-5 flex items-center justify-between gap-2">
        {lastDate && (
          <span
            className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-mono-tab"
            data-testid={`stock-quote-date-${stock.ticker.toLowerCase()}`}
          >
            <Calendar className="w-3 h-3" />
            {fmtDate(lastDate)}
          </span>
        )}
        <Button
          variant="outline"
          size="sm"
          className="flex-1"
          onClick={() => onOpenInsight && onOpenInsight(stock)}
          data-testid={`open-insight-${stock.ticker.toLowerCase()}`}
        >
          Nota analítica
          <ChevronRight className="w-3.5 h-3.5 ml-1" />
        </Button>
      </div>
    </div>
  );
}
